import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Snackbar,
  Alert,
  Box,
  Chip,
  Divider,
  Avatar,
  LinearProgress,
} from "@mui/material";

import Documentos from "./Documentos";
import RegistroMantenedora from "./RegistroMantenedora";
import { useNavigate } from "react-router-dom";
import {
  School as SchoolIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";

import { api } from "@api/api";
import { ajustarTamanhoFonteEscola } from "../../../estilos/global";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";

export default function PaginaProcessos() {
  const [escolas, setEscolas] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [processType, setProcessType] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showDocumentos, setShowDocumentos] = useState(false);
  const [showRegistro, setShowRegistro] = useState(true);
  const [mantenedoraRegistrada, setMantenedoraRegistrada] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [selectedSchoolDetails, setSelectedSchoolDetails] = useState(null);
  const [isForAllSchools, setIsForAllSchools] = useState(null);
  const [selectedProcessDescription, setSelectedProcessDescription] =
    useState("");
  const [mantenedoraAtiva, setMantenedoraAtiva] = useState(false);
  const [aguardandoAprovacao, setAguardandoAprovacao] = useState(false);
  const [mensagemEscoActivate, setMensagemEscoActivate] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    checkUserStatus();
    fetchSchoolsUser();
    fetchGeneralProcesses();
  }, []);

  const fetchSchoolsUser = async () => {
    try {
      setLoading(true);

      const response = await axios.get(api.listUserSchools(), {
        withCredentials: true,
      });

      const schools = response.data.escolas || [];

      const uniqueSchools = schools.reduce((acc, current) => {
        const found = acc.find((item) => item.nome === current.nome);
        return found ? acc : [...acc, current];
      }, []);

      setEscolas(uniqueSchools);
    } catch (error) {
      console.error("Error fetching schools:", error);
      setSnackbarMessage("Error fetching schools");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchGeneralProcesses = async () => {
    try {
      const response = await axios.get(api.listGeneralProcesses(), {
        withCredentials: true,
      });

      const processos = response.data;

      const processosOrdenados = [...processos].sort((a, b) =>
        (a.nome || "").localeCompare(b.nome || "", "pt-BR", {
          sensitivity: "base",
        }),
      );

      const escoActivateResponse = await axios.get(api.listUserSchools(), {
        withCredentials: true,
      });

      const { EscoActivate } = escoActivateResponse.data;

      if (EscoActivate) {
        setProcessTypes(processosOrdenados);
        setMensagemEscoActivate("");
      } else {
        const filteredProcesses = processosOrdenados.filter(
          (p) => p.nome === "Credenciamento",
        );

        setProcessTypes(filteredProcesses);

        setMensagemEscoActivate(
          "Para mais tipos de processo, registre uma escola em sua mantenedora.",
        );
      }
    } catch (error) {
      console.error("Error fetching processes:", error);
      setSnackbarMessage("Error fetching processes");
      setSnackbarOpen(true);
    }
  };

  const checkUserStatus = async () => {
    try {
      const response = await axios.get(api.verify(), {
        withCredentials: true,
      });

      const { isRegistered, ativo } = response.data;

      if (!isRegistered) {
        setMantenedoraRegistrada(false);
        setShowRegistro(true);
        setAguardandoAprovacao(false);
      } else {
        setMantenedoraRegistrada(true);
        setShowRegistro(false);

        if (ativo) {
          setMantenedoraAtiva(true);
          setAguardandoAprovacao(false);
        } else {
          setMantenedoraAtiva(false);
          setAguardandoAprovacao(true);
        }
      }
    } catch (error) {
      console.error("Error checking user status:", error);
      setSnackbarMessage("Error checking user status");
      setSnackbarOpen(true);
    }
  };

  const handleSchoolChange = (e) => {
    const schoolId = e.target.value;

    setSelectedSchool(schoolId);

    const school = escolas.find((s) => s.id === schoolId);
    setSelectedSchoolDetails(school);
  };

  const handleProcessTypeChange = (e) => {
    const selectedType = e.target.value;

    setProcessType(selectedType);

    const selectedProcess = processTypes.find((p) => p.id === selectedType);

    setIsForAllSchools(
      selectedProcess ? selectedProcess.para_todas_escolas : null,
    );

    setSelectedProcessDescription(
      selectedProcess ? selectedProcess.descricao : "",
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isFormValid()) {
      const criadoEm = new Date().toISOString();

      const data = {
        processType,
        selectedSchool: isForAllSchools ? null : selectedSchool,
        criadoEm,
      };

      sessionStorage.setItem("processData", JSON.stringify(data));
      setSnackbarMessage("Processo salvo temporariamente!");
      setSnackbarOpen(true);
      navigate("/novoProcesso/documentos");
    } else {
      setSnackbarMessage("Por favor, preencha todos os campos obrigatórios!");
      setSnackbarOpen(true);
    }
  };

  const isFormValid = () => {
    return processType && (isForAllSchools || selectedSchool);
  };

  if (loading) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          mt: 8,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Carregando informações...
          </Typography>

          <LinearProgress color="primary" />
        </Box>
      </Container>
    );
  }

  return (
    <TransicaoPagina variante="navegacao">
      <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 6 }}>
        <div>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                mb: 1,
                background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {showRegistro ? "Registrar Mantenedora" : "Criar Novo Processo"}
            </Typography>

            {!showRegistro && (
              <Typography variant="subtitle1" color="text.secondary">
                Preencha os dados abaixo para iniciar um novo processo
              </Typography>
            )}
          </Box>
        </div>

        <div>
          <Paper
            elevation={4}
            sx={{
              borderRadius: "16px",
              padding: { xs: "20px", md: "30px" },
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(to bottom right, #ffffff, #f5f9ff)",
            }}
          >
            {showRegistro ? (
              <RegistroMantenedora onNext={() => setShowRegistro(false)} />
            ) : aguardandoAprovacao ? (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                  Seu cadastro está em análise
                </Typography>

                <Typography variant="body1">
                  Por favor, aguarde a aprovação do seu cadastro para continuar.
                </Typography>
              </Box>
            ) : showDocumentos ? (
              <Documentos
                processType={processType}
                selectedSchool={selectedSchool}
                uploadedDocs={uploadedDocs}
                isConfirmed={isConfirmed}
                onUpload={() => {}}
                onReturn={() => setShowDocumentos(false)}
                onRegister={() => {}}
              />
            ) : (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <div>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel sx={{ color: "text.secondary" }}>
                          Tipo de Processo
                        </InputLabel>

                        <Select
                          value={processType}
                          onChange={handleProcessTypeChange}
                          label="Tipo de Processo"
                          sx={{
                            borderRadius: "8px",
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        >
                          {processTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <DescriptionIcon
                                  sx={{ mr: 1, color: "primary.main" }}
                                />
                                {type.nome}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Grid>

                  {mensagemEscoActivate && (
                    <Grid item xs={12}>
                      <div>
                        <Chip
                          label={mensagemEscoActivate}
                          color="warning"
                          variant="outlined"
                          sx={{ width: "100%" }}
                        />
                      </div>
                    </Grid>
                  )}

                  {selectedProcessDescription && (
                    <Grid item xs={12}>
                      <div>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "8px",
                            backgroundColor: "background.paper",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            gutterBottom
                          >
                            Descrição do Processo Selecionado:
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            {selectedProcessDescription}
                          </Typography>
                        </Box>
                      </div>
                    </Grid>
                  )}

                  {isForAllSchools === false && (
                    <Grid item xs={12}>
                      <div>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel sx={{ color: "text.secondary" }}>
                            Selecione a Escola
                          </InputLabel>

                          <Select
                            value={selectedSchool}
                            onChange={handleSchoolChange}
                            label="Selecione a Escola"
                            sx={{
                              borderRadius: "8px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                          >
                            {escolas.map((school) => (
                              <MenuItem key={school.id} value={school.id}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      mr: 1,
                                      bgcolor: "primary.main",
                                    }}
                                  >
                                    <SchoolIcon sx={{ fontSize: 16 }} />
                                  </Avatar>

                                  {school.nome}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <div>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          size="large"
                          disabled={!isFormValid()}
                          sx={{
                            borderRadius: "8px",
                            px: 6,
                            py: 1.5,
                            fontSize: ajustarTamanhoFonteEscola("0.875rem"),
                            fontWeight: "bold",
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                            "&:hover": {
                              boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Continuar
                        </Button>
                      </Box>
                    </div>
                  </Grid>
                </Grid>
              </form>
            )}
          </Paper>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="info"
            sx={{
              width: "100%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              borderRadius: "8px",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </TransicaoPagina>
  );
}
